import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import { t } from "i18next";
import MyButton from "./UI/Button";

// Заголовки колонок Excel -> поля заказа (регистр и лишние пробелы не важны)
const HEADERS = {
  tracking_number: [
    "номер отслеживания (трек)",
    "номер отслеживания",
    "трек",
    "трек-код",
  ],
  gross_weight: ["физ.вес", "физ. вес"],
  width: ["ширина"],
  height: ["высота"],
  length: ["длина"],
};

const norm = (s) => String(s).trim().toLowerCase().replace(/\s+/g, " ");

const num = (v) => {
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

function pick(row, names) {
  for (const key of Object.keys(row)) {
    if (names.includes(norm(key))) return row[key];
  }
  return "";
}

function parseRow(row) {
  return {
    tracking_number: String(pick(row, HEADERS.tracking_number)).trim(),
    gross_weight: num(pick(row, HEADERS.gross_weight)),
    width: num(pick(row, HEADERS.width)),
    height: num(pick(row, HEADERS.height)),
    length: num(pick(row, HEADERS.length)),
  };
}

/**
 * Props:
 *  visible  - открыта ли модалка
 *  onHide   - закрыть модалку
 *  onRow    - async (row) => void | { skipped: true }
 *             Если заказа нет в системе, верните { skipped: true }.
 *             При ошибке бросьте Error.
 *  onDone   - ({ total, success, skipped, failed }) => void
 *  title    - заголовок окна
 */
export default function ExcelImportModal({
  visible,
  onHide,
  onRow,
  onDone,
  title,
}) {
  const inputRef = useRef(null);

  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | ready | running | finished
  const [done, setDone] = useState(0);
  const [failed, setFailed] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [readError, setReadError] = useState("");

  const running = status === "running";

  const reset = () => {
    setFileName("");
    setRows([]);
    setStatus("idle");
    setDone(0);
    setFailed([]);
    setSkipped([]);
    setReadError("");
  };

  const handleHide = () => {
    if (running) return;
    reset();
    onHide?.();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // чтобы можно было выбрать тот же файл ещё раз
    if (!file) return;

    reset();
    setFileName(file.name);

    try {
      const wb = XLSX.read(await file.arrayBuffer());
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const parsed = XLSX.utils
        .sheet_to_json(sheet, { defval: "" })
        .map(parseRow)
        .filter((r) => r.tracking_number);

      if (!parsed.length) {
        setReadError(
          t("Не найдено ни одной строки с трек-кодом. Проверьте заголовки колонок.")
        );
        return;
      }

      setRows(parsed);
      setStatus("ready");
    } catch (err) {
      setReadError(t("Не удалось прочитать файл"));
    }
  };

  const run = async () => {
    setStatus("running");
    setDone(0);
    setFailed([]);
    setSkipped([]);

    const errors = [];
    const skippedList = [];

    for (let i = 0; i < rows.length; i++) {
      try {
        const res = await onRow(rows[i]);
        if (res?.skipped) skippedList.push(rows[i].tracking_number);
      } catch (err) {
        errors.push({
          track: rows[i].tracking_number,
          reason: err?.message || t("Ошибка"),
        });
      }
      setDone(i + 1);
    }

    setFailed(errors);
    setSkipped(skippedList);
    setStatus("finished");
    onDone?.({
      total: rows.length,
      success: rows.length - errors.length - skippedList.length,
      skipped: skippedList,
      failed: errors,
    });
  };

  const percent = rows.length ? Math.round((done / rows.length) * 100) : 0;
  const successCount = rows.length - failed.length - skipped.length;

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
      {status !== "finished" && (
        <MyButton
          label={fileName ? t("Выбрать другой файл") : t("Выбрать файл")}
          outlined
          disabled={running}
          onClick={() => inputRef.current?.click()}
        />
      )}
      {status === "ready" && (
        <MyButton label={`${t("Загрузить")} (${rows.length})`} onClick={run} />
      )}
      {status === "finished" && (
        <MyButton
          label={t("Загрузить ещё")}
          outlined
          onClick={() => {
            reset();
            inputRef.current?.click();
          }}
        />
      )}
      <MyButton label={t("Закрыть")} disabled={running} onClick={handleHide} />
    </div>
  );

  return (
    <Dialog
      header={title ?? t("Добавить с таблицы Excel")}
      visible={visible}
      onHide={handleHide}
      closable={!running}
      style={{ width: "720px", maxWidth: "95vw" }}
      footer={footer}
      draggable={false}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: "none" }}
        onChange={handleFile}
      />

      {status === "idle" && !readError && (
        <p style={{ margin: 0 }}>
          {t(
            "Выберите Excel файл с колонками: Номер отслеживания (трек), Физ.вес, Ширина, Высота, Длина. Заказы, которых нет в системе, будут пропущены."
          )}
        </p>
      )}

      {readError && <p style={{ margin: 0, color: "#d32f2f" }}>{readError}</p>}

      {fileName && !readError && (
        <p style={{ margin: "0 0 12px" }}>
          <b>{t("Файл")}:</b> {fileName} &nbsp;|&nbsp; <b>{t("Строк")}:</b>{" "}
          {rows.length}
        </p>
      )}

      {/* Предпросмотр первых строк */}
      {(status === "ready" || running) && (
        <div style={{ maxHeight: 260, overflow: "auto", marginBottom: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={cell}>{t("Трек")}</th>
                <th style={cell}>{t("Физ.вес")}</th>
                <th style={cell}>{t("Ш × В × Д")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 50).map((r, i) => (
                <tr key={`${r.tracking_number}-${i}`}>
                  <td style={cell}>{r.tracking_number}</td>
                  <td style={cell}>{r.gross_weight}</td>
                  <td style={cell}>
                    {r.width} × {r.height} × {r.length}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 50 && (
            <p style={{ margin: "8px 0 0", opacity: 0.7 }}>
              {t("Показаны первые 50 строк")}
            </p>
          )}
        </div>
      )}

      {/* Прогресс */}
      {(running || status === "finished") && (
        <div style={{ marginTop: 8 }}>
          <ProgressBar value={percent} />
          <p style={{ margin: "8px 0 0" }}>
            {t("Обработано")}: {done} / {rows.length}
          </p>
        </div>
      )}

      {/* Итог */}
      {status === "finished" && (
        <div style={{ marginTop: 12 }}>
          <p style={{ margin: 0, color: "#2e7d32" }}>
            <b>
              {t("Принято и обновлено")}: {successCount}
            </b>
          </p>

          {skipped.length > 0 && (
            <>
              <p style={{ margin: "8px 0 4px", opacity: 0.8 }}>
                <b>
                  {t("Пропущено (нет в системе)")}: {skipped.length}
                </b>
              </p>
              <div style={{ maxHeight: 120, overflow: "auto", opacity: 0.8 }}>
                {skipped.map((track, i) => (
                  <span key={`${track}-${i}`} style={{ display: "block" }}>
                    {track}
                  </span>
                ))}
              </div>
            </>
          )}

          {failed.length > 0 && (
            <>
              <p style={{ margin: "8px 0 4px", color: "#d32f2f" }}>
                <b>
                  {t("Ошибки")}: {failed.length}
                </b>
              </p>
              <div style={{ maxHeight: 160, overflow: "auto" }}>
                {failed.map((f, i) => (
                  <p key={`${f.track}-${i}`} style={{ margin: "2px 0" }}>
                    <b>{f.track}</b>: {f.reason}
                  </p>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

const cell = {
  padding: "6px 8px",
  borderBottom: "1px solid #e0e0e0",
  fontSize: 14,
};